import { useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { UploadCloud, Loader2, X as XIcon, Image as ImageIcon } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ACCEPT = "image/jpeg,image/png,image/webp,image/gif";

export const ImageUploader = ({ value, onChange, label = "Recipe image" }) => {
  const [uploading, setUploading] = useState(false);
  const [dragging, setDragging] = useState(false);
  const inputRef = useRef(null);

  const handleFile = async (file) => {
    if (!file) return;
    if (!ACCEPT.split(",").includes(file.type)) {
      toast.error("Only JPG, PNG, WEBP or GIF allowed.");
      return;
    }
    if (file.size > 8 * 1024 * 1024) {
      toast.error("Image is larger than 8 MB.");
      return;
    }
    const token = localStorage.getItem("maati_admin_token") || "";
    const form = new FormData();
    form.append("file", file);
    setUploading(true);
    try {
      const { data } = await axios.post(`${API}/admin/upload`, form, {
        headers: {
          "X-Admin-Token": token,
          "Content-Type": "multipart/form-data",
        },
      });
      onChange(data.url);
      toast.success("Image uploaded.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed.");
    } finally {
      setUploading(false);
    }
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    const f = e.dataTransfer?.files?.[0];
    if (f) handleFile(f);
  };

  const onPick = (e) => {
    const f = e.target.files?.[0];
    if (f) handleFile(f);
    e.target.value = "";
  };

  const clear = () => onChange("");

  return (
    <div data-testid="image-uploader">
      <span className="block text-xs uppercase tracking-[0.2em] text-mo-ink-soft mb-2">
        {label}
      </span>

      {value ? (
        <div className="relative rounded-xl overflow-hidden border border-[#5C5248]/20 bg-[#F9F6F0] group">
          <img
            src={value}
            alt="Recipe preview"
            className="w-full h-64 object-cover"
            data-testid="image-uploader-preview"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent flex items-end p-4 gap-2">
            <button
              type="button"
              onClick={() => inputRef.current?.click()}
              disabled={uploading}
              data-testid="image-uploader-replace"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-mo-cream text-mo-ink text-xs font-medium hover:bg-white transition-colors disabled:opacity-60"
            >
              {uploading ? <Loader2 className="animate-spin" size={13} /> : <UploadCloud size={13} />}
              Replace
            </button>
            <button
              type="button"
              onClick={clear}
              data-testid="image-uploader-clear"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-mo-terracotta text-mo-cream text-xs font-medium hover:bg-[#8F2B17] transition-colors"
            >
              <XIcon size={13} /> Remove
            </button>
          </div>
        </div>
      ) : (
        <label
          onDragEnter={(e) => {
            e.preventDefault();
            setDragging(true);
          }}
          onDragOver={(e) => {
            e.preventDefault();
            setDragging(true);
          }}
          onDragLeave={() => setDragging(false)}
          onDrop={onDrop}
          data-testid="image-uploader-dropzone"
          className={`flex flex-col items-center justify-center gap-3 h-48 rounded-xl border-2 border-dashed cursor-pointer transition-colors ${
            dragging
              ? "border-mo-terracotta bg-[#B33922]/5"
              : "border-[#5C5248]/30 bg-[#F9F6F0] hover:border-mo-terracotta hover:bg-[#B33922]/5"
          }`}
        >
          {uploading ? (
            <>
              <Loader2 className="animate-spin text-mo-terracotta" size={28} />
              <span className="text-sm text-mo-ink-soft">Uploading to Cloudinary…</span>
            </>
          ) : (
            <>
              <div className="w-12 h-12 rounded-full bg-[#B33922]/10 flex items-center justify-center text-mo-terracotta">
                <UploadCloud size={22} />
              </div>
              <div className="text-center px-4">
                <div className="text-sm font-medium text-mo-ink">
                  Drop an image or click to browse
                </div>
                <div className="text-xs text-mo-ink-soft mt-1">
                  JPG · PNG · WEBP · GIF · up to 8 MB
                </div>
              </div>
            </>
          )}
          <input
            ref={inputRef}
            type="file"
            accept={ACCEPT}
            className="hidden"
            onChange={onPick}
            data-testid="image-uploader-input"
          />
        </label>
      )}

      <details className="mt-2 text-xs text-mo-ink-soft">
        <summary className="cursor-pointer inline-flex items-center gap-1.5 hover:text-mo-terracotta">
          <ImageIcon size={12} /> or paste a URL
        </summary>
        <input
          type="text"
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          placeholder="https://..."
          data-testid="image-uploader-url"
          className="mt-2 w-full px-4 py-2.5 rounded-lg border border-[#5C5248]/30 bg-[#F9F6F0] text-mo-ink text-sm"
        />
      </details>
    </div>
  );
};
